import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../axios/axiosInstance";
import { showToast } from "./ToastSlice";

// ─── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchActivePlans = createAsyncThunk(
  "billing/fetchActivePlans",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/billing/plans");
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch plans");
    }
  }
);

export const fetchCurrentSubscription = createAsyncThunk(
  "billing/fetchCurrentSubscription",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/billing/subscription");
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch subscription");
    }
  }
);

export const validateCoupon = createAsyncThunk(
  "billing/validateCoupon",
  async (code, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post("/billing/coupon/validate", { code });
      dispatch(showToast({ message: "Coupon applied successfully!", variant: "success" }));
      return response.data.coupon;
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Invalid coupon code", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const createSetupIntent = createAsyncThunk(
  "billing/createSetupIntent",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post("/billing/payment-methods/setup-intent");
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to create setup intent");
    }
  }
);

export const fetchPaymentMethods = createAsyncThunk(
  "billing/fetchPaymentMethods",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/billing/payment-methods");
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch payment methods");
    }
  }
);

export const attachPaymentMethod = createAsyncThunk(
  "billing/attachPaymentMethod",
  async ({ paymentMethodId, setAsDefault }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post("/billing/payment-methods/attach", { paymentMethodId, setAsDefault });
      dispatch(showToast({ message: "Card added successfully!", variant: "success" }));
      return response.data;
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Failed to add card", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const removePaymentMethod = createAsyncThunk(
  "billing/removePaymentMethod",
  async (paymentMethodId, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/billing/payment-methods/${paymentMethodId}`);
      dispatch(showToast({ message: "Card removed successfully", variant: "success" }));
      return paymentMethodId;
    } catch (err) {
      dispatch(showToast({ message: "Failed to remove card", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const setDefaultPaymentMethod = createAsyncThunk(
  "billing/setDefaultPaymentMethod",
  async (paymentMethodId, { rejectWithValue, dispatch }) => {
    try {
      await api.put("/billing/payment-methods/set-default", { paymentMethodId });
      dispatch(showToast({ message: "Default card updated", variant: "success" }));
      return paymentMethodId;
    } catch (err) {
      dispatch(showToast({ message: "Failed to update default card", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const subscribeToPlan = createAsyncThunk(
  "billing/subscribeToPlan",
  async (subscribeData, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post("/billing/subscribe", subscribeData);
      dispatch(showToast({ message: "Subscription created successfully!", variant: "success" }));
      return response.data;
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Failed to subscribe", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const activateSubscription = createAsyncThunk(
  "billing/activateSubscription",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post("/billing/subscription/activate");
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to activate subscription");
    }
  }
);

export const cancelSubscription = createAsyncThunk(
  "billing/cancelSubscription",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post("/billing/cancel");
      dispatch(showToast({ message: response.data.message, variant: "success" }));
      return response.data;
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Failed to cancel subscription", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const upgradePlan = createAsyncThunk(
  "billing/upgradePlan",
  async ({ planId, billingPeriod, couponCode }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post("/billing/upgrade", { planId, billingPeriod, couponCode: couponCode || undefined });
      dispatch(showToast({ message: "Plan upgraded successfully!", variant: "success" }));
      return response.data;
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Failed to upgrade plan", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const fetchAgentQuota = createAsyncThunk(
  "billing/fetchAgentQuota",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/billing/agents/quota");
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch agent quota");
    }
  }
);

export const previewAgentSeats = createAsyncThunk(
  "billing/previewAgentSeats",
  async (agentCount, { rejectWithValue }) => {
    try {
      const response = await api.post("/billing/agents/preview", { agentCount });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to preview cost");
    }
  }
);

export const updateAgentSeats = createAsyncThunk(
  "billing/updateAgentSeats",
  async ({ agentCount, couponCode } = {}, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post("/billing/agents/update", { agentCount, couponCode });
      dispatch(showToast({ message: response.data.message || "Agent seats updated", variant: "success" }));
      return response.data;
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Failed to update agent seats", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const previewPlanChange = createAsyncThunk(
  "billing/previewPlanChange",
  async ({ planId, billingPeriod, couponCode }, { rejectWithValue }) => {
    try {
      const response = await api.post("/billing/upgrade/preview", { planId, billingPeriod, couponCode: couponCode || undefined });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to preview plan change");
    }
  }
);

export const fetchInvoices = createAsyncThunk(
  "billing/fetchInvoices",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("/billing/invoices", { params });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch invoices");
    }
  }
);

export const fetchMyRetentionCoupon = createAsyncThunk(
  "billing/fetchMyRetentionCoupon",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/billing/retention-coupon/mine");
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch retention coupon");
    }
  }
);

export const fetchRetentionSettings = createAsyncThunk(
  "billing/fetchRetentionSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/billing/retention-settings");
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch retention settings");
    }
  }
);

export const generateRetentionCoupon = createAsyncThunk(
  "billing/generateRetentionCoupon",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post("/billing/retention-coupon/generate");
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to generate retention coupon";
      dispatch(showToast({ message: msg, variant: "danger" }));
      return rejectWithValue(msg);
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const billingSlice = createSlice({
  name: "billing",
  initialState: {
    // Plans
    plans: [],
    plansLoading: false,
    plansLastFetched: null,

    // Subscription
    subscription: null,
    planStatus: "none",
    trialEndsAt: null,
    trialStartedAt: null,
    subscriptionLoading: false,
    subscriptionLastFetched: null,

    // Payment methods
    paymentMethods: [],
    defaultPaymentMethodId: null,
    paymentMethodsLoading: false,
    paymentMethodsLastFetched: null,
    setupIntentClientSecret: null,
    actionLoading: false,

    // Coupon
    appliedCoupon: null,
    couponLoading: false,

    // Agent seats
    agentQuota: null, // { agentCount, usedCount, remaining, agentPrice }
    agentQuotaLastFetched: null,
    agentQuotaLoading: false,

    // Upgrade preview (prorated charge calculation)
    upgradePreview: null,
    upgradePreviewLoading: false,

    // Invoices
    invoices: [],
    invoicesTotal: 0,
    invoicesTotalPages: 1,
    invoicesLoading: false,
    billingSummary: { totalPaid: 0, totalDue: 0, totalBills: 0 },

    // Retention coupon
    retentionDiscountPercent: 30,
    retentionCoupon: null,         // set after successful generation
    myRetentionCoupon: null,       // user's pre-existing retention coupon (if any)
    retentionLifecycle: { hasGenerated: false, hasUsed: false, generatedAt: null, usedAt: null },
    retentionLoading: false,

    error: null,
  },
  reducers: {
    clearAppliedCoupon: (state) => { state.appliedCoupon = null; },
    clearSetupIntent: (state) => { state.setupIntentClientSecret = null; },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      // Plans
      .addCase(fetchActivePlans.pending, (state) => { state.plansLoading = true; })
      .addCase(fetchActivePlans.fulfilled, (state, action) => {
        state.plansLoading = false;
        state.plans = action.payload.plans || [];
        state.plansLastFetched = Date.now();
      })
      .addCase(fetchActivePlans.rejected, (state) => { state.plansLoading = false; })

      // Subscription
      .addCase(fetchCurrentSubscription.pending, (state) => { state.subscriptionLoading = true; })
      .addCase(fetchCurrentSubscription.fulfilled, (state, action) => {
        state.subscriptionLoading = false;
        state.subscription = action.payload.subscription;
        state.planStatus = action.payload.planStatus;
        state.trialEndsAt = action.payload.trialEndsAt;
        state.trialStartedAt = action.payload.trialStartedAt;
        state.subscriptionLastFetched = Date.now();
      })
      .addCase(fetchCurrentSubscription.rejected, (state) => { state.subscriptionLoading = false; })

      // Coupon
      .addCase(validateCoupon.pending, (state) => { state.couponLoading = true; })
      .addCase(validateCoupon.fulfilled, (state, action) => {
        state.couponLoading = false;
        state.appliedCoupon = action.payload;
      })
      .addCase(validateCoupon.rejected, (state) => { state.couponLoading = false; })

      // Setup Intent
      .addCase(createSetupIntent.fulfilled, (state, action) => {
        state.setupIntentClientSecret = action.payload.clientSecret;
      })

      // Payment Methods
      .addCase(fetchPaymentMethods.pending, (state) => { state.paymentMethodsLoading = true; })
      .addCase(fetchPaymentMethods.fulfilled, (state, action) => {
        state.paymentMethodsLoading = false;
        state.paymentMethods = action.payload.paymentMethods || [];
        state.defaultPaymentMethodId = action.payload.defaultPaymentMethodId || null;
        state.paymentMethodsLastFetched = Date.now();
      })
      .addCase(fetchPaymentMethods.rejected, (state) => { state.paymentMethodsLoading = false; })

      .addCase(attachPaymentMethod.pending, (state) => { state.actionLoading = true; })
      .addCase(attachPaymentMethod.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.paymentMethods = action.payload.paymentMethods || [];
        state.setupIntentClientSecret = null;
      })
      .addCase(attachPaymentMethod.rejected, (state) => { state.actionLoading = false; })

      .addCase(removePaymentMethod.fulfilled, (state, action) => {
        state.paymentMethods = (state.paymentMethods || []).filter((pm) => pm.id !== action.payload);
      })

      .addCase(setDefaultPaymentMethod.fulfilled, (state, action) => {
        state.defaultPaymentMethodId = action.payload;
      })

      // Subscribe
      .addCase(subscribeToPlan.pending, (state) => { state.actionLoading = true; })
      .addCase(subscribeToPlan.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.subscription = action.payload.subscription;
        state.planStatus = "active";
      })
      .addCase(subscribeToPlan.rejected, (state) => { state.actionLoading = false; })

      // Activate (after frontend payment confirmation)
      .addCase(activateSubscription.fulfilled, (state, action) => {
        state.subscription = action.payload.subscription;
        state.planStatus = "active";
      })

      // Cancel — subscription is scheduled to end at period end, NOT cancelled immediately.
      // planStatus stays "active"; access continues until the billing period ends.
      .addCase(cancelSubscription.fulfilled, (state) => {
        if (state.subscription) {
          state.subscription.cancelAtPeriodEnd = true;
          state.subscription.cancelledAt = new Date().toISOString();
        }
        // Do NOT set planStatus to "cancelled" here — user still has access until period end.
        // The backend webhook will update planStatus when Stripe fires the subscription.deleted event.
      })

      // Upgrade
      .addCase(upgradePlan.pending, (state) => { state.actionLoading = true; })
      .addCase(upgradePlan.fulfilled, (state, action) => {
        state.actionLoading = false;
        // Merge the returned subscription so period dates, planId, billingPeriod are all fresh
        if (action.payload.subscription) {
          state.subscription = action.payload.subscription;
          state.planStatus = "active";
        }
      })
      .addCase(upgradePlan.rejected, (state) => { state.actionLoading = false; })

      // Agent quota
      .addCase(fetchAgentQuota.pending, (state) => { state.agentQuotaLoading = true; })
      .addCase(fetchAgentQuota.fulfilled, (state, action) => {
        state.agentQuota = action.payload;
        state.agentQuotaLastFetched = Date.now();
        state.agentQuotaLoading = false;
      })
      .addCase(fetchAgentQuota.rejected, (state) => { state.agentQuotaLoading = false; })
      .addCase(updateAgentSeats.fulfilled, (state, action) => {
        if (action.payload.subscription) {
          state.subscription = action.payload.subscription;
          if (state.agentQuota) state.agentQuota.agentCount = action.payload.subscription.agentCount;
        }
      })

      // Upgrade preview
      .addCase(previewPlanChange.pending, (state) => {
        state.upgradePreviewLoading = true;
        state.upgradePreview = null;
      })
      .addCase(previewPlanChange.fulfilled, (state, action) => {
        state.upgradePreviewLoading = false;
        state.upgradePreview = action.payload;
      })
      .addCase(previewPlanChange.rejected, (state) => {
        state.upgradePreviewLoading = false;
      })

      // Invoices
      .addCase(fetchInvoices.pending, (state) => { state.invoicesLoading = true; })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.invoicesLoading = false;
        state.invoices = action.payload.invoices || [];
        state.invoicesTotal = action.payload.total || 0;
        state.invoicesTotalPages = action.payload.totalPages || 1;
        state.billingSummary = action.payload.summary || { totalPaid: 0, totalDue: 0, totalBills: 0 };
      })
      .addCase(fetchInvoices.rejected, (state) => { state.invoicesLoading = false; })

      // My retention coupon
      .addCase(fetchMyRetentionCoupon.fulfilled, (state, action) => {
        state.myRetentionCoupon = action.payload.coupon || null;
      })

      // Retention settings — hydrates coupon lifecycle flags and existing coupon on page load
      .addCase(fetchRetentionSettings.fulfilled, (state, action) => {
        state.retentionDiscountPercent = action.payload.retentionDiscountPercent || 30;
        if (action.payload.retentionCoupon) {
          state.retentionLifecycle = action.payload.retentionCoupon;
        }
        if (action.payload.existingRetentionCoupon) {
          state.myRetentionCoupon = action.payload.existingRetentionCoupon;
        }
      })

      // Generate retention coupon
      .addCase(generateRetentionCoupon.pending, (state) => { state.retentionLoading = true; })
      .addCase(generateRetentionCoupon.fulfilled, (state, action) => {
        state.retentionLoading = false;
        state.retentionCoupon = action.payload.coupon || null;
        // Also store as myRetentionCoupon so the upgrade modal can see it immediately
        state.myRetentionCoupon = action.payload.coupon || null;
      })
      .addCase(generateRetentionCoupon.rejected, (state) => { state.retentionLoading = false; });
  },
});

export const { clearAppliedCoupon, clearSetupIntent, clearError } = billingSlice.actions;
export default billingSlice.reducer;
